import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../services/supabase';

const generateId = () => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }
    // Fallback for older browsers
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
        (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
};

// ─── Types ───────────────────────────────────────────────────

export type LabelSize = '76x130' | '100x100' | '100x130' | '100x150' | '100x170' | '50x30' | '40x30';

export const LABEL_SIZES: Record<LabelSize, { w: number; h: number; label: string }> = {
    '76x130': { w: 76, h: 130, label: '76 × 130 mm' },
    '100x100': { w: 100, h: 100, label: '100 × 100 mm' },
    '100x130': { w: 100, h: 130, label: '100 × 130 mm' },
    '100x150': { w: 100, h: 150, label: '100 × 150 mm' },
    '100x170': { w: 100, h: 170, label: '100 × 170 mm' },
    '50x30': { w: 50, h: 30, label: '50 × 30 mm' },
    '40x30': { w: 40, h: 30, label: '40 × 30 mm' },
};

export type ElementType = 'text' | 'barcode' | 'qrcode' | 'rect' | 'line' | 'image' | 'multiline' | 'table';

/** Data fields available for binding */
export const DATA_FIELDS = [
    { key: 'tracking_no', label: '运单号' },
    { key: 'receiver_name', label: '收件人姓名' },
    { key: 'receiver_phone', label: '收件人电话' },
    { key: 'receiver_address', label: '收件人地址' },
    { key: 'sender_name', label: '发件人姓名' },
    { key: 'sender_phone', label: '发件人电话' },
    { key: 'sender_address', label: '发件人地址' },
    { key: 'shipper_name', label: '发货客户' },
    { key: 'weight', label: '重量 (kg)' },
    { key: 'volume_weight', label: '体积重 (kg)' },
    { key: 'dimensions', label: '尺寸 (L*W*H)' },
    { key: 'pieces', label: '件数' },
    { key: 'batch_no', label: '批次号' },
    { key: 'transport_mode', label: '运输方式' },
    { key: 'item_category', label: '物品类型' },
    { key: 'package_tag', label: '包裹标签' },
    { key: 'remark', label: '备注' },
    { key: 'date', label: '日期' },
    { key: 'print_time', label: '打印时间' },
    { key: 'operator', label: '操作员' },
    { key: 'custom', label: '自定义文本' },
    { key: 'item_desc', label: '货物描述' },
    { key: 'quantity', label: '数量' },
    { key: 'unit_price', label: '单价' },
    { key: 'total_price', label: '总价' },
] as const;

export type DataFieldKey = typeof DATA_FIELDS[number]['key'];

export interface LabelElement {
    id: string;
    type: ElementType;
    x: number;       // mm from left
    y: number;       // mm from top
    width: number;   // mm
    height: number;  // mm
    // Text props
    text?: string;
    fontSize?: number;   // pt
    fontWeight?: 'normal' | 'bold';
    textAlign?: 'left' | 'center' | 'right';
    dataField?: DataFieldKey;  // bind to data
    // Barcode/QR props
    barcodeFormat?: 'CODE128' | 'CODE39' | 'EAN13' | 'UPC';
    // Line props
    lineWidth?: number;
    // Rect props
    borderWidth?: number;
    filled?: boolean;
    // Image
    imageUrl?: string;
    // Common
    rotation?: number;
    // New: color & font
    color?: string;
    fontFamily?: string;
    // New: multiline
    multilineText?: string;
    lineHeight?: number;
    // New: table
    tableRows?: number;
    tableCols?: number;
    tableData?: string[][];
    tableHeaderRow?: boolean;
}

export interface LabelTemplate {
    id: string;
    name: string;
    size: LabelSize;
    offsetX?: number; // mm
    offsetY?: number; // mm
    elements: LabelElement[];
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
}

/** Pre-composed modular blocks */
export const LABEL_BLOCKS = [
    {
        id: 'block_receiver',
        name: '收件人信息块',
        icon: 'contact_page',
        elements: (w: number): Omit<LabelElement, 'id'>[] => [
            { type: 'text', x: 0, y: 0, width: 20, height: 5, text: '收件人', fontSize: 8, fontWeight: 'bold' },
            { type: 'text', x: 0, y: 6, width: w, height: 8, dataField: 'receiver_name', fontSize: 14, fontWeight: 'bold' },
            { type: 'text', x: 0, y: 15, width: w, height: 5, dataField: 'receiver_phone', fontSize: 10 },
            { type: 'text', x: 0, y: 21, width: w, height: 12, dataField: 'receiver_address', fontSize: 9 },
        ]
    },
    {
        id: 'block_sender',
        name: '发件人信息块',
        icon: 'person',
        elements: (w: number): Omit<LabelElement, 'id'>[] => [
            { type: 'text', x: 0, y: 0, width: 20, height: 5, text: '发件人', fontSize: 8, fontWeight: 'bold' },
            { type: 'text', x: 0, y: 6, width: w, height: 5, dataField: 'sender_name', fontSize: 9 },
            { type: 'text', x: 0, y: 12, width: w, height: 10, dataField: 'sender_address', fontSize: 8 },
        ]
    },
    {
        id: 'block_logistics',
        name: '物流条码块',
        icon: 'barcode',
        elements: (w: number): Omit<LabelElement, 'id'>[] => [
            { type: 'barcode', x: 0, y: 0, width: w, height: 18, dataField: 'tracking_no', barcodeFormat: 'CODE128' },
            { type: 'text', x: 0, y: 20, width: w, height: 6, dataField: 'tracking_no', fontSize: 12, fontWeight: 'bold', textAlign: 'center' },
        ]
    },
    {
        id: 'block_goods',
        name: '货物参数块',
        icon: 'inventory_2',
        elements: (w: number): Omit<LabelElement, 'id'>[] => [
            { type: 'text', x: 0, y: 0, width: 25, height: 6, dataField: 'weight', fontSize: 11, fontWeight: 'bold' },
            { type: 'text', x: 30, y: 0, width: 20, height: 6, dataField: 'pieces', fontSize: 10, text: '件数: ' },
            { type: 'text', x: 0, y: 8, width: 40, height: 5, dataField: 'dimensions', fontSize: 8 },
        ]
    },
    {
        id: 'block_meta',
        name: '打印页脚块',
        icon: 'info',
        elements: (w: number): Omit<LabelElement, 'id'>[] => [
            { type: 'line', x: 0, y: 0, width: w, height: 0, lineWidth: 0.5 },
            { type: 'text', x: 0, y: 2, width: 30, height: 4, dataField: 'print_time', fontSize: 7, textAlign: 'left' },
            { type: 'text', x: w - 30, y: 2, width: 30, height: 4, dataField: 'operator', fontSize: 7, textAlign: 'right' },
        ]
    }
] as const;

// ─── Default Templates ──────────────────────────────────────

const createDefaultTemplate = (size: LabelSize): LabelTemplate => {
    const { w, h } = LABEL_SIZES[size];
    const id = generateId();

    // ─── Specialized Layout for 100x170 ───
    if (size === '100x170') {
        return {
            id,
            name: `标准快递面单 (100x170)`,
            size,
            isDefault: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            elements: [
                // Sender section
                { id: 'el_send_title', type: 'text', x: 5, y: 5, width: 20, height: 5, text: '发件人', fontSize: 8, fontWeight: 'bold' },
                { id: 'el_send_name', type: 'text', x: 5, y: 12, width: w - 10, height: 6, dataField: 'sender_name', fontSize: 10 },
                { id: 'el_send_addr', type: 'text', x: 5, y: 18, width: w - 10, height: 10, dataField: 'sender_address', fontSize: 9 },

                { id: 'el_divider1', type: 'line', x: 3, y: 30, width: w - 6, height: 0, lineWidth: 1 },

                // Receiver section
                { id: 'el_recv_title', type: 'text', x: 5, y: 32, width: 20, height: 5, text: '收件人', fontSize: 8, fontWeight: 'bold' },
                { id: 'el_recv_name', type: 'text', x: 5, y: 39, width: w - 10, height: 8, dataField: 'receiver_name', fontSize: 14, fontWeight: 'bold' },
                { id: 'el_recv_phone', type: 'text', x: 5, y: 47, width: w - 10, height: 6, dataField: 'receiver_phone', fontSize: 11 },
                { id: 'el_recv_addr', type: 'text', x: 5, y: 54, width: w - 10, height: 16, dataField: 'receiver_address', fontSize: 10 },

                { id: 'el_divider2', type: 'line', x: 3, y: 72, width: w - 6, height: 0, lineWidth: 1 },

                // Logistics / Barcode
                { id: 'el_barcode', type: 'barcode', x: 10, y: 76, width: w - 20, height: 25, dataField: 'tracking_no', barcodeFormat: 'CODE128' },
                { id: 'el_tracking', type: 'text', x: 10, y: 105, width: w - 20, height: 8, dataField: 'tracking_no', fontSize: 16, fontWeight: 'bold', textAlign: 'center' },

                { id: 'el_divider3', type: 'line', x: 3, y: 116, width: w - 6, height: 0, lineWidth: 1 },

                // Cargo info (Weight, Pieces, Categories)
                { id: 'el_weight_tag', type: 'text', x: 5, y: 120, width: 12, height: 6, text: '计费重:', fontSize: 10, fontWeight: 'bold' },
                { id: 'el_weight', type: 'text', x: 18, y: 119, width: 25, height: 8, dataField: 'weight', fontSize: 14, fontWeight: 'bold' },
                { id: 'el_pieces_tag', type: 'text', x: 45, y: 120, width: 10, height: 6, text: '件数:', fontSize: 10, fontWeight: 'bold' },
                { id: 'el_pieces', type: 'text', x: 56, y: 119, width: 20, height: 8, dataField: 'pieces', fontSize: 14, fontWeight: 'bold' },

                { id: 'el_dim_tag', type: 'text', x: 5, y: 128, width: 12, height: 6, text: '尺寸:', fontSize: 10 },
                { id: 'el_dim', type: 'text', x: 18, y: 128, width: 60, height: 6, dataField: 'dimensions', fontSize: 10 },

                { id: 'el_remark', type: 'text', x: 5, y: 136, width: w - 10, height: 12, dataField: 'remark', fontSize: 10 },

                { id: 'el_divider4', type: 'line', x: 3, y: 150, width: w - 6, height: 0, lineWidth: 1 },

                // Footer (QR code, Print Time, Operator)
                { id: 'el_qr', type: 'qrcode', x: w - 25, y: 151, width: 18, height: 18, dataField: 'tracking_no' },
                { id: 'el_print_time', type: 'text', x: 5, y: 160, width: 40, height: 4, dataField: 'print_time', fontSize: 8 },
                { id: 'el_operator', type: 'text', x: 5, y: 165, width: 40, height: 4, dataField: 'operator', fontSize: 8 },
            ]
        };
    }

    // ─── Specialized Layout for 40x30 (Compact Logistics) ───
    if (size === '40x30') {
        return {
            id,
            name: `创造货物面单 (40x30)`,
            size,
            isDefault: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            elements: [
                // Top line: Shipper Name (User's Customer)
                {
                    id: 'el_shipper',
                    type: 'text',
                    x: 2,
                    y: 2,
                    width: w - 4,
                    height: 5,
                    dataField: 'shipper_name',
                    fontSize: 10,
                    fontWeight: 'bold',
                    textAlign: 'left',
                },
                // Divider
                {
                    id: 'el_div1',
                    type: 'line',
                    x: 2,
                    y: 7,
                    width: w - 4,
                    height: 0,
                    lineWidth: 0.5,
                },
                // Small Barcode
                {
                    id: 'el_barcode',
                    type: 'barcode',
                    x: 2,
                    y: 9,
                    width: w - 4,
                    height: 10,
                    dataField: 'tracking_no',
                    barcodeFormat: 'CODE128',
                },
                // Tracking number text (small/medium)
                {
                    id: 'el_tracking',
                    type: 'text',
                    x: 2,
                    y: 20,
                    width: w - 4,
                    height: 4,
                    dataField: 'tracking_no',
                    fontSize: 9,
                    fontWeight: 'bold',
                    textAlign: 'center',
                },
                // Divider
                {
                    id: 'el_div2',
                    type: 'line',
                    x: 2,
                    y: 25,
                    width: w - 4,
                    height: 0,
                    lineWidth: 0.5,
                },
                // Weight row
                {
                    id: 'el_weight_tag',
                    type: 'text',
                    x: 2,
                    y: 26,
                    width: 5,
                    height: 3,
                    text: 'W:',
                    fontSize: 8,
                },
                {
                    id: 'el_weight',
                    type: 'text',
                    x: 6,
                    y: 26,
                    width: 15,
                    height: 4,
                    dataField: 'weight',
                    fontSize: 9,
                    fontWeight: 'bold',
                },
                // Pieces row
                {
                    id: 'el_pieces_tag',
                    type: 'text',
                    x: 22,
                    y: 26,
                    width: 8,
                    height: 3,
                    text: 'QTY:',
                    fontSize: 8,
                },
                {
                    id: 'el_pieces',
                    type: 'text',
                    x: 30,
                    y: 26,
                    width: 8,
                    height: 4,
                    dataField: 'pieces',
                    fontSize: 9,
                    fontWeight: 'bold',
                },
            ],
        };
    }

    return {
        id,
        name: `标准快递面单 (${LABEL_SIZES[size].label})`,
        size,
        offsetX: 0,
        offsetY: 0,
        isDefault: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        elements: [
            // ... (Rest of the standard template from lines 152-313 remains the same)
            // Barcode at top
            {
                id: 'el_barcode',
                type: 'barcode',
                x: 5,
                y: 5,
                width: w - 10,
                height: 18,
                dataField: 'tracking_no',
                barcodeFormat: 'CODE128',
            },
            // Tracking number text
            {
                id: 'el_tracking',
                type: 'text',
                x: 5,
                y: 25,
                width: w - 10,
                height: 6,
                text: '',
                dataField: 'tracking_no',
                fontSize: 12,
                fontWeight: 'bold',
                textAlign: 'center',
            },
            // Divider line
            {
                id: 'el_divider1',
                type: 'line',
                x: 3,
                y: 33,
                width: w - 6,
                height: 0,
                lineWidth: 1,
            },
            // Receiver section title
            {
                id: 'el_recv_title',
                type: 'text',
                x: 5,
                y: 35,
                width: 20,
                height: 5,
                text: '收件人',
                fontSize: 8,
                fontWeight: 'bold',
                textAlign: 'left',
                dataField: 'custom',
            },
            // Receiver name
            {
                id: 'el_recv_name',
                type: 'text',
                x: 5,
                y: 41,
                width: w - 10,
                height: 6,
                dataField: 'receiver_name',
                fontSize: 14,
                fontWeight: 'bold',
                textAlign: 'left',
            },
            // Receiver phone
            {
                id: 'el_recv_phone',
                type: 'text',
                x: 5,
                y: 48,
                width: w - 10,
                height: 5,
                dataField: 'receiver_phone',
                fontSize: 10,
                fontWeight: 'normal',
                textAlign: 'left',
            },
            // Receiver address
            {
                id: 'el_recv_addr',
                type: 'text',
                x: 5,
                y: 54,
                width: w - 10,
                height: 12,
                dataField: 'receiver_address',
                fontSize: 9,
                fontWeight: 'normal',
                textAlign: 'left',
            },
            // Divider
            {
                id: 'el_divider2',
                type: 'line',
                x: 3,
                y: 68,
                width: w - 6,
                height: 0,
                lineWidth: 1,
            },
            // Sender section title
            {
                id: 'el_send_title',
                type: 'text',
                x: 5,
                y: 70,
                width: 20,
                height: 5,
                text: '发件人',
                fontSize: 8,
                fontWeight: 'bold',
                textAlign: 'left',
                dataField: 'custom',
            },
            // Sender name
            {
                id: 'el_send_name',
                type: 'text',
                x: 5,
                y: 76,
                width: w - 10,
                height: 5,
                dataField: 'sender_name',
                fontSize: 9,
                fontWeight: 'normal',
                textAlign: 'left',
            },
            // Sender address
            {
                id: 'el_send_addr',
                type: 'text',
                x: 5,
                y: 82,
                width: w - 10,
                height: 10,
                dataField: 'sender_address',
                fontSize: 8,
                fontWeight: 'normal',
                textAlign: 'left',
            },
            // Weight display
            {
                id: 'el_weight',
                type: 'text',
                x: 5,
                y: h - 30,
                width: 30,
                height: 6,
                dataField: 'weight',
                fontSize: 11,
                fontWeight: 'bold',
                textAlign: 'left',
            },
            // QR Code
            {
                id: 'el_qr',
                type: 'qrcode',
                x: w - 25,
                y: h - 30,
                width: 20,
                height: 20,
                dataField: 'tracking_no',
            },
        ],
    };
};

// ─── Store ───────────────────────────────────────────────────

interface LabelTemplateState {
    templates: LabelTemplate[];
    activeTemplateId: string | null;
    /** Map of feature name (e.g. 'cargo_create') to the preferred template ID */
    featureTemplates: Record<string, string>;

    // Database sync actions
    fetchFromDb: () => Promise<void>;
    saveToDb: (template: LabelTemplate) => Promise<void>;
    deleteFromDb: (id: string) => Promise<void>;
    syncDefaultToDb: (id: string, size: LabelSize) => Promise<void>;

    // Actions
    addTemplate: (template: LabelTemplate) => void;
    updateTemplate: (id: string, updates: Partial<LabelTemplate>) => void;
    deleteTemplate: (id: string) => void;
    duplicateTemplate: (id: string) => string;
    setDefault: (id: string) => void;
    setActiveTemplate: (id: string | null) => void;
    setFeatureTemplate: (feature: string, templateId: string) => void;
    getTemplatesBySize: (size: LabelSize) => LabelTemplate[];
    getDefaultTemplate: (size: LabelSize) => LabelTemplate | undefined;
    createBlankTemplate: (size: LabelSize, name?: string) => string;
    initDefaults: () => void;
}

export const useLabelTemplateStore = create<LabelTemplateState>()(
    persist(
        (set, get) => ({
            templates: [],
            activeTemplateId: null,
            featureTemplates: {},

            fetchFromDb: async () => {
                const { data: userData } = await supabase.auth.getUser();
                if (!userData?.user) return;
                const { data, error } = await supabase
                    .from('print_templates')
                    .select('*')
                    .eq('user_id', userData.user.id);
                if (!error && data) {
                    const dbTemplates: LabelTemplate[] = data.map(row => ({
                        id: row.id,
                        name: row.name,
                        size: row.size as LabelSize,
                        isDefault: row.is_default,
                        elements: row.elements as any,
                        createdAt: row.created_at,
                        updatedAt: row.updated_at
                    }));

                    // Optional: Merge with local if needed, but DB source of truth is preferred:
                    if (dbTemplates.length > 0) {
                        set({ templates: dbTemplates });
                    }
                }
            },

            saveToDb: async (template) => {
                const { data: userData } = await supabase.auth.getUser();
                if (!userData?.user) return;
                await supabase.from('print_templates').upsert({
                    id: template.id,
                    user_id: userData.user.id,
                    name: template.name,
                    size: template.size,
                    is_default: template.isDefault,
                    elements: template.elements as any,
                    created_at: template.createdAt,
                    updated_at: template.updatedAt,
                }, { onConflict: 'id' });
            },

            deleteFromDb: async (id) => {
                await supabase.from('print_templates').delete().eq('id', id);
            },

            syncDefaultToDb: async (id, size) => {
                const { data: userData } = await supabase.auth.getUser();
                if (!userData?.user) return;
                await supabase
                    .from('print_templates')
                    .update({ is_default: false })
                    .eq('user_id', userData.user.id)
                    .eq('size', size);

                if (id) {
                    await supabase
                        .from('print_templates')
                        .update({ is_default: true })
                        .eq('user_id', userData.user.id)
                        .eq('id', id);
                }
            },

            setFeatureTemplate: (feature, templateId) =>
                set((s) => ({
                    featureTemplates: { ...s.featureTemplates, [feature]: templateId }
                })),

            addTemplate: (template) => {
                set((s) => ({ templates: [...s.templates, template] }));
                get().saveToDb(template);
            },

            updateTemplate: (id, updates) => {
                set((s) => ({
                    templates: s.templates.map((t) =>
                        t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
                    ),
                }));
                const updated = get().templates.find(t => t.id === id);
                if (updated) get().saveToDb(updated);
            },

            deleteTemplate: (id) => {
                set((s) => ({ templates: s.templates.filter((t) => t.id !== id) }));
                get().deleteFromDb(id);
            },

            duplicateTemplate: (id) => {
                const original = get().templates.find((t) => t.id === id);
                if (!original) return '';
                const newId = generateId();
                const copy: LabelTemplate = {
                    ...JSON.parse(JSON.stringify(original)),
                    id: newId,
                    name: original.name + ' (副本)',
                    isDefault: false,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                set((s) => ({ templates: [...s.templates, copy] }));
                get().saveToDb(copy);
                return newId;
            },

            setDefault: (id) => {
                set((s) => {
                    const target = s.templates.find((t) => t.id === id);
                    if (!target) return s;
                    return {
                        templates: s.templates.map((t) =>
                            t.size === target.size
                                ? { ...t, isDefault: t.id === id }
                                : t
                        ),
                    };
                });

                const target = get().templates.find((t) => t.id === id);
                if (target) {
                    get().syncDefaultToDb(id, target.size);
                }
            },

            setActiveTemplate: (id) => set({ activeTemplateId: id }),

            getTemplatesBySize: (size) => get().templates.filter((t) => t.size === size),

            getDefaultTemplate: (size) => get().templates.find((t) => t.size === size && t.isDefault),

            createBlankTemplate: (size, name) => {
                const newId = generateId();
                const template: LabelTemplate = {
                    id: newId,
                    name: name || `新模板 (${LABEL_SIZES[size].label})`,
                    size,
                    elements: [],
                    isDefault: false,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                set((s) => ({ templates: [...s.templates, template] }));
                get().saveToDb(template);
                return newId;
            },

            initDefaults: () => {
                // Fetch from DB first, if available
                get().fetchFromDb().then(() => {
                    const { templates } = get();
                    const sizes: LabelSize[] = ['76x130', '100x100', '100x130', '100x150', '100x170', '40x30'];
                    const newTemplates: LabelTemplate[] = [];

                    for (const size of sizes) {
                        if (!templates.some((t) => t.size === size)) {
                            newTemplates.push(createDefaultTemplate(size));
                        }
                    }

                    if (newTemplates.length > 0) {
                        set((s) => ({ templates: [...s.templates, ...newTemplates] }));
                        newTemplates.forEach(t => get().saveToDb(t));
                    }
                });
            },
        }),
        {
            name: 'hedui-label-templates',
            storage: createJSONStorage(() => localStorage),
        }
    )
);
